import { useId, type ReactElement, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  className?: string;
  /** A form control that accepts `id` and `aria-describedby`. */
  children: ReactElement<{ id?: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }>;
}

export function Field({ label, hint, error, required, className, children }: FieldProps) {
  const reactId = useId();
  const id = (children.props as { id?: string }).id ?? `field-${reactId}`;
  const describedBy: string[] = [];
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  if (hintId) describedBy.push(hintId);
  if (errorId) describedBy.push(errorId);

  const childWithProps = cloneWithProps(children, {
    id,
    'aria-describedby': describedBy.length ? describedBy.join(' ') : undefined,
    'aria-invalid': Boolean(error),
  });

  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-zinc-900">
          {label} {required && <span className="text-red-600">*</span>}
        </label>
      )}
      {childWithProps}
      {hint && !error && (
        <p id={hintId} className="text-xs text-zinc-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

function cloneWithProps<P>(el: ReactElement<P>, extra: Partial<P> & Record<string, unknown>): ReactNode {
  return {
    ...el,
    props: { ...(el.props as object), ...extra },
  } as ReactNode;
}
